import HolographicCard from '@/components/base/HolographicCard';

export default function AlphaSigmaSection() {

  return (
    <section id="genesisos" className="py-20 relative">
      {/* Background elements */}
      <div className="absolute top-20 left-20 w-40 h-40 bg-gradient-to-br from-purple-400/5 to-transparent rounded-full blur-2xl"></div>
      <div className="absolute bottom-20 right-20 w-32 h-32 bg-gradient-to-br from-cyan-400/5 to-transparent rounded-full blur-2xl"></div>

      <div className="container mx-auto px-6 relative z-10">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-20">
            <h2 className="text-5xl md:text-6xl font-bold mb-6 font-orbitron">
              <span
                className="text-white"
                style={{
                  textShadow: `
                    -1px -1px 0 #8b5cf6,
                    1px -1px 0 #8b5cf6,
                    -1px 1px 0 #8b5cf6,
                    1px 1px 0 #8b5cf6,
                    -2px -2px 0 #06b6d4,
                    2px -2px 0 #06b6d4,
                    -2px 2px 0 #06b6d4,
                    2px 2px 0 #06b6d4
                  `
                }}
              >
                GenesisOS
              </span>
            </h2>
            <p className="text-2xl text-white/90 font-medium mb-4">
              Lifecycle Operating System for Governed AI Agents
            </p>
          </div>

          {/* Feature Cards */}
          <div className="grid md:grid-cols-3 gap-8 mb-16">
            <HolographicCard className="p-8 text-center">
              <div className="w-16 h-16 bg-gradient-to-br from-pink-400/20 to-pink-600/20 rounded-full flex items-center justify-center border border-pink-400/30 mx-auto mb-6">
                <i className="ri-fingerprint-line text-pink-400 text-2xl"></i>
              </div>
              <h3 className="text-xl font-semibold text-white mb-4">
                Compiled Identity, Verified Every Turn
              </h3>
              <p className="text-white/70 leading-relaxed">
                Your agent&apos;s identity is cryptographically compiled at genesis and verified on every interaction. Same values, same personality, same boundaries across sessions, channels, and AI providers. You can always prove it&apos;s the same agent.
              </p>
            </HolographicCard>

            <HolographicCard className="p-8 text-center">
              <div className="w-16 h-16 bg-gradient-to-br from-cyan-400/20 to-cyan-600/20 rounded-full flex items-center justify-center border border-cyan-400/30 mx-auto mb-6">
                <i className="ri-brain-line text-cyan-400 text-2xl"></i>
              </div>
              <h3 className="text-xl font-semibold text-white mb-4">
                Memory That Compounds, Governed by Design
              </h3>
              <p className="text-white/70 leading-relaxed">
                Knowledge accumulates through a 10-step promotion pipeline with grounding verification. Bad data is rejected. Good data compounds over time. Your agent gets more valuable the longer it operates.
              </p>
            </HolographicCard>

            <HolographicCard className="p-8 text-center">
              <div className="w-16 h-16 bg-gradient-to-br from-purple-400/20 to-purple-600/20 rounded-full flex items-center justify-center border border-purple-400/30 mx-auto mb-6">
                <i className="ri-shield-check-line text-purple-400 text-2xl"></i>
              </div>
              <h3 className="text-xl font-semibold text-white mb-4">
                Autonomous Work, Full Auditability
              </h3>
              <p className="text-white/70 leading-relaxed">
                Agents pursue goals over days and weeks, collaborate in governed teams, and operate across five channels. Every decision, every tool call, every delegation is traced and auditable in real time.
              </p>
            </HolographicCard>
          </div>

          {/* Detailed Card */}
          <HolographicCard className="p-8 md:p-12">
            <div className="grid md:grid-cols-3 gap-12 items-center min-h-[400px]">
              {/* Left side - Main content */}
              <div className="md:col-span-2">
                <h2 className="text-3xl md:text-4xl font-bold mb-8">
                  <span className="text-white">A Foundation for </span><span className="text-cyan-400">Trustworthy AI</span>
                </h2>

                <p className="text-white/80 text-lg leading-relaxed mb-8">
                  GenesisOS is the first platform where AI agents are born with intentional identity, governed by deterministic rules, and designed to grow more valuable over time. The architecture separates identity from memory, enforces governance at every turn, and makes every agent&apos;s behavior auditable and portable across providers. The result: AI agents you can actually trust, that remember your context, pursue your goals, and stay the same agent tomorrow that they are today.
                </p>

                <div className="space-y-6">
                  <div className="flex items-start space-x-4">
                    <div className="w-3 h-3 bg-cyan-400 rounded-full mt-2 flex-shrink-0"></div>
                    <div>
                      <h4 className="text-white font-semibold text-lg mb-1">Substrate-Agnostic Portability</h4>
                      <p className="text-white/70 text-sm">Run your agents on Claude, GPT, or Grok. Switch providers without losing identity, memory, or governance. The substrate is commodity. Your agent is yours.</p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4">
                    <div className="w-3 h-3 bg-cyan-400 rounded-full mt-2 flex-shrink-0"></div>
                    <div>
                      <h4 className="text-white font-semibold text-lg mb-1">Deterministic Governance Runtime</h4>
                      <p className="text-white/70 text-sm">Every turn passes through a 10-phase governed pipeline. Identity loaded, memory retrieved, output verified, knowledge promoted. No ungoverned path exists.</p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4">
                    <div className="w-3 h-3 bg-cyan-400 rounded-full mt-2 flex-shrink-0"></div>
                    <div>
                      <h4 className="text-white font-semibold text-lg mb-1">Compound Value Over Time</h4>
                      <p className="text-white/70 text-sm">Agents accumulate expertise through governed memory. Teams build collaborative intelligence through delegation. The longer you use GenesisOS, the more valuable your agents become.</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Right side - Concentric circles logo and link */}
              <div className="flex flex-col items-center justify-center text-center h-full">
                <div
                  className="w-40 h-40 rounded-full flex items-center justify-center border mb-8 transition-all duration-300"
                  style={{
                    background: 'linear-gradient(135deg, rgba(6, 182, 212, 0.2), rgba(139, 92, 246, 0.2))',
                    borderColor: 'rgba(6, 182, 212, 0.3)'
                  }}
                >
                  <svg
                    width="120"
                    height="120"
                    viewBox="0 0 120 120"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    {/* Outer ring */}
                    <circle
                      cx="60"
                      cy="60"
                      r="56"
                      stroke="rgba(103, 232, 249, 0.55)"
                      strokeWidth="1.5"
                      fill="none"
                    />
                    {/* Middle ring */}
                    <circle
                      cx="60"
                      cy="60"
                      r="38"
                      stroke="rgba(34, 211, 238, 0.8)"
                      strokeWidth="1.5"
                      fill="none"
                    />
                    {/* Inner filled circle with pulse */}
                    <circle
                      cx="60"
                      cy="60"
                      r="18"
                      fill="#06b6d4"
                      style={{ animation: 'innerPulse 8s ease-in-out infinite' }}
                    />
                  </svg>
                </div>
                <a
                  href="https://genesisos.ai"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="whitespace-nowrap px-6 py-3 bg-gradient-to-r from-cyan-400 to-purple-500 text-white font-semibold rounded-lg hover:shadow-lg hover:shadow-cyan-400/25 transition-all duration-300 cursor-pointer"
                >
                  Try GenesisOS
                </a>
              </div>
            </div>
          </HolographicCard>
        </div>
      </div>

      <style>{`
        @keyframes colorPulse {
          0%, 100% {
            background: linear-gradient(135deg, rgba(6, 182, 212, 0.3), rgba(6, 182, 212, 0.1));
            border-color: rgba(6, 182, 212, 0.4);
          }
          50% {
            background: linear-gradient(135deg, rgba(139, 92, 246, 0.3), rgba(139, 92, 246, 0.1));
            border-color: rgba(139, 92, 246, 0.4);
          }
        }
        @keyframes innerPulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
      `}</style>
    </section>
  );
}
